import { useState } from 'react'
import { AdvancedDisclosure, Button, Drawer, Input, Popover, Select, StatePanel, StatusBadge } from '@/components/common'
import { manifestStatusLabel } from './nativeSportsbookManifest'
import styles from './nativeSportsbook.module.css'
import workspaceLayout from './nativeWorkspace.module.css'

const TERMINAL_LABELS = new Set(['Awaiting independent approval', 'Production release published', 'Production approval unavailable'])

// The label shown on both the trigger and the popover's confirm button, so the
// action they take is never a surprise once the popover opens.
function publishLabel(workspace, isAuthor) {
  if (workspace.publishChannel !== 'production') return `Publish to ${workspace.publishChannel}`
  const status = workspace.selected?.status
  if (status === 'draft') return 'Request production approval'
  if (status === 'pending_approval') return isAuthor ? 'Awaiting independent approval' : 'Approve production release'
  if (status === 'approved') return 'Publish production'
  return status === 'published' ? 'Production release published' : 'Production approval unavailable'
}

// Which mutation the confirm button runs, or null for a terminal state with no action.
function publishAction(workspace) {
  if (workspace.publishChannel !== 'production') return workspace.publishMutation
  const status = workspace.selected?.status
  if (status === 'draft') return workspace.requestApprovalMutation
  if (status === 'pending_approval') return workspace.approveMutation
  if (status === 'approved') return workspace.publishMutation
  return null
}

// Why the confirm button is disabled, in the operator's terms; null when it
// is enabled and the consequence line applies instead.
function publishBlocker(workspace) {
  if (!workspace.selected) return 'Save a first draft before publishing.'
  if (workspace.dirty) return 'Save your changes before publishing.'
  const issues = workspace.validation && !workspace.validation.valid ? workspace.validation.issues.length : 0
  if (issues) return `Fix ${issues} ${issues === 1 ? 'issue' : 'issues'} before publishing.`
  return null
}

function publishConsequence(workspace, isAuthor) {
  const revision = workspace.selected?.revision
  if (workspace.publishChannel !== 'production') {
    return `Signs revision ${revision} and publishes it to ${workspace.publishChannel}. Apps on this channel pick it up on their next refresh.`
  }
  const status = workspace.selected?.status
  if (status === 'draft') return `Sends revision ${revision} for independent approval. A different publisher must approve before it can go to production.`
  if (status === 'pending_approval' && !isAuthor) return `Approves revision ${revision} for production.`
  if (status === 'approved') return `Signs revision ${revision} and publishes it to production.`
  return null
}

export function PublishControl({ workspace, canPublish, isAuthor }) {
  const [open, setOpen] = useState(false)
  const label = publishLabel(workspace, isAuthor)
  const triggerDisabled = !canPublish || TERMINAL_LABELS.has(label)
  const activeMutation = publishAction(workspace)
  const blocker = publishBlocker(workspace)
  const consequence = blocker ?? publishConsequence(workspace, isAuthor)
  const releaseLocked = !canPublish || workspace.publishMutation.isPending || workspace.requestApprovalMutation.isPending || workspace.approveMutation.isPending
  const reasonRequired = workspace.publishChannel === 'production' && workspace.selected?.status === 'draft'
  const confirmDisabled = Boolean(blocker) || !activeMutation || activeMutation.isPending || (reasonRequired && !workspace.publishReason.trim())

  return (
    <Popover
      placement="top-end"
      label="Publish"
      open={open}
      onOpenChange={setOpen}
      trigger={<Button size="compact" disabled={triggerDisabled}>{label}</Button>}
    >
      <div className={styles.publishPopover}>
        <strong>{workspace.selected ? `Revision ${workspace.selected.revision}` : 'New draft'}</strong>
        <Select size="compact" aria-label="Release channel" value={workspace.publishChannel} disabled={releaseLocked} onChange={(event) => workspace.setPublishChannel(event.target.value)}>
          <option value="preview">Preview</option>
          <option value="staging">Staging</option>
          <option value="production">Production</option>
        </Select>
        <Input
          size="compact"
          aria-label="Release note"
          placeholder={workspace.publishChannel === 'production' ? 'Reason for approval (required)' : 'Release note (optional)'}
          value={workspace.publishReason}
          maxLength={240}
          disabled={!canPublish}
          onChange={(event) => workspace.setPublishReason(event.target.value)}
        />
        {consequence ? <p className={styles.publishConsequence} data-tone={blocker ? 'warning' : undefined}>{consequence}</p> : null}
        <Button
          size="compact"
          disabled={confirmDisabled}
          loading={activeMutation?.isPending}
          onClick={() => activeMutation.mutate(undefined, { onSuccess: () => setOpen(false) })}
        >
          {label}
        </Button>
      </div>
    </Popover>
  )
}

function PublicationRow({ publication, onSelect }) {
  return (
    <button type="button" className={styles.ledgerRow} onClick={() => onSelect(publication)}>
      <span>
        <b>m{publication.manifest_version}</b>
        <small>{publication.channel} · release #{publication.channel_sequence}</small>
        {publication.publication_reason ? <small>{publication.publication_reason}</small> : null}
      </span>
      <code>{publication.content_sha256.slice(0, 12)}</code>
      <time dateTime={publication.published_at}>{new Date(publication.published_at).toLocaleString()}</time>
      {publication.is_safe ? <StatusBadge type="success" size="small">known safe</StatusBadge> : null}
    </button>
  )
}

function RollbackCard({ workspace, canPublish, canOperate }) {
  const target = workspace.rollbackTarget
  if (!target) return null
  return (
    <div className={styles.rollbackCard}>
      <strong>Manifest v{target.manifest_version} · {target.content_sha256.slice(0, 12)}</strong>
      <Input size="compact" aria-label="Required reason" placeholder="Required reason" value={workspace.rollbackReason} maxLength={240} disabled={!canPublish && !canOperate} onChange={(event) => workspace.setRollbackReason(event.target.value)} />
      <div className={styles.rollbackActions}>
        <Button size="compact" variant="danger" disabled={!canPublish || !workspace.rollbackReason.trim() || workspace.rollbackMutation.isPending} onClick={() => workspace.rollbackMutation.mutate()}>Restore this version</Button>
        <Button size="compact" variant="secondary" disabled={!canOperate || !workspace.rollbackReason.trim() || workspace.setSafeMutation.isPending} onClick={() => workspace.setSafeMutation.mutate()}>Mark known safe</Button>
        <Button size="compact" variant="ghost" onClick={() => workspace.setRollbackTarget(null)}>Cancel</Button>
      </div>
    </div>
  )
}

export function ReleasesDrawer({ open, onClose, workspace, canPublish, canOperate, registry }) {
  const safeConfigured = workspace.publications.some((publication) => publication.is_safe)
  return (
    <Drawer
      open={open}
      side="right"
      title="Releases"
      description={`Signed releases per channel, earlier drafts, and recovery. Component library v${registry.registry_version}.`}
      onClose={onClose}
    >
      <div className={styles.drawerBody}>
        <section aria-label="Signed releases" className={styles.drawerSection}>
          <h3>Signed releases</h3>
          <div className={styles.ledger}>
            {workspace.publications.map((publication) => <PublicationRow key={publication.id} publication={publication} onSelect={workspace.setRollbackTarget} />)}
            {!workspace.publicationsQuery.isLoading && !workspace.publications.length ? <StatePanel state="empty" title="No releases yet" description="Save and publish a Home version to create the first release." compact /> : null}
          </div>
          <RollbackCard workspace={workspace} canPublish={canPublish} canOperate={canOperate} />
        </section>
        <section aria-label="Draft history" className={styles.drawerSection}>
          <h3>Draft history</h3>
          <div className={styles.versionList}>
            {workspace.manifests.map((manifest) => (
              <button type="button" key={manifest.id} className={styles.version} aria-current={workspace.selected?.id === manifest.id ? 'true' : undefined} onClick={() => workspace.selectManifest(manifest)}>
                <span>
                  Revision {manifest.revision}
                  {manifest.theme_revision != null ? <span className={workspaceLayout.identityMeta}> · theme r{manifest.theme_revision}</span> : null}
                </span>
                <StatusBadge size="small" type={manifest.status === 'published' ? 'success' : 'default'}>{manifestStatusLabel(manifest.status)}</StatusBadge>
                <code>{manifest.content_sha256.slice(0, 12)}</code>
              </button>
            ))}
          </div>
        </section>
        <section aria-label="Recovery" className={styles.drawerSection}>
          <h3>Recovery</h3>
          <Button variant="secondary" className={styles.fullWidth} disabled={!canOperate || !safeConfigured || workspace.safeModeMutation.isPending} onClick={() => workspace.safeModeMutation.mutate()}>Restore the known-safe Home</Button>
          <AdvancedDisclosure label="Advanced release tools">
            <Button variant="secondary" className={styles.fullWidth} disabled={!canOperate || workspace.reconcileMutation.isPending} onClick={() => workspace.reconcileMutation.mutate()}>Reconcile pending commands</Button>
          </AdvancedDisclosure>
        </section>
      </div>
    </Drawer>
  )
}
